import {
    Box,
    Center, Container,
    Image,
    Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay,
    Spinner,
    useDisclosure,
} from "@chakra-ui/react";
import React, {ForwardedRef, forwardRef, MutableRefObject, useImperativeHandle, useRef} from "react";
import {QueryKey, useQuery} from "react-query";
import {Navigate} from "react-router-dom";

import {requestMultiMedia} from "../Api";
import {useTheme} from "../hooks/ThemeProvider";
import {Paths, ValidAudioFormats, ValidImageFormats} from "../model/Constants";
import {extractData} from "../Utils";
import AudioPlayer, {AudioPlayerUpdate} from "./AudioPlayer";
import Markdown from "./Markdown";

export type ResourceUpdate = {
  updateSource: (sourceUrl: string) => void;
  stopPlayer: () => void;
  startPlayer: () => void;
}

type ResourceProps = {
    resource: string;
    text?: string;
    compact?: boolean;
    timesPlayed?: MutableRefObject<number>;
    optionIds?: MutableRefObject<string[]>;
    subdirectory?: string;
    conditions?: string;
    onAudioPlayed?: (timesPlayed: number) => void;

    // audioPlayer props
    onAudioEnded?: () => void;
    hidePlayButton?: boolean;
    maxNumberOfReproductions?: number;
    noOutline?:boolean;
    autoPlay?:boolean;
    hidden?:boolean;

    small?:boolean;
    icon?:boolean;
};

const RenderResource = forwardRef((props: ResourceProps, ref: ForwardedRef<ResourceUpdate>) => {
  const audioPlayerRef = useRef<AudioPlayerUpdate>(null);

  useImperativeHandle(ref, () => ({
    updateSource(blobUrl:string) {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.updateSource(blobUrl);
      }
    },
    stopPlayer() {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.stopPlayer();
      }
    },
    startPlayer() {
      if (audioPlayerRef.current) {
        audioPlayerRef.current.startPlayer();
      }
    }
  }));

  const theme = useTheme().theme;
    const { isOpen, onOpen, onClose } = useDisclosure()
    const publicSrc = (props.resource || "");//.replace(/\s/g, '');
    const extension = publicSrc.split('.').pop() || "";

    let sourceUrl = "";
    let sourceDir = "";
    let queryKey : QueryKey = ["public", publicSrc];

    if (ValidImageFormats.includes(extension)) {
        queryKey = ["public", "images", publicSrc];
        sourceDir = "images/";
    } else if (ValidAudioFormats.includes(extension)) {
        queryKey = ["public", "audio", publicSrc];
        sourceDir = "audio/";
    }

    const sourceQuery = useQuery(queryKey, () =>
        requestMultiMedia(publicSrc, extension, sourceDir, props.subdirectory || "").then(extractData),
      {retry: true}
    );

    if (sourceQuery.isError) {
        return <Navigate to={Paths.ERROR} />;
    } else if (!sourceQuery.data) {
        return (
            <Center height="200px">
                <Spinner />
            </Center>
        );
    } else {
        //Image maxBlockSize={"2xl"}
        if (!sourceUrl && sourceQuery.data.size > 0) {
            sourceUrl = URL.createObjectURL(sourceQuery.data);
        }
        if (sourceUrl && ValidImageFormats.includes(extension)) {
            return (
                <Box display='flex' alignItems='center' justifyContent='center'>
                    { props.text != null && <Markdown
                        content={props.text}
                        compact={props.compact}
                    /> }
                    <Container onClick={onOpen} justifyItems='center'
                               borderColor='black' border='solid'
                               cursor='zoom-in'>
                        <Image src={sourceUrl}
                           alt={publicSrc}
                           objectFit='contain'
                           maxHeight={props.icon ? "100px" : (props.small ? "200px" : "530px")}
                           draggable={false}
                           pointerEvents={"none"}
                        />
                        {/*_dark={{ filter: "invert(1) hue-rotate(180deg)" }}*/}
                    </Container>
                    <Modal  isOpen={isOpen} onClose={onClose} isCentered
                    scrollBehavior="inside"
                           size={"5xl"} >
                        <ModalOverlay />
                        <ModalContent
                          filter= {'grayscale('+theme.IMG_GRAYSCALE+') contrast('+theme.IMG_CONTRAST+')'}
                        >
                          {/*_dark={{ filter: "invert(1) hue-rotate(180deg)" }}*/}
                            <ModalCloseButton />
                            <ModalBody style={{
                                 justifyItems:'center',
                            }}>
                                <Image src={sourceUrl}
                                       alt={publicSrc}
                                       maxWidth='4xl'
                                       maxHeight='800px'
                                       minWidth='3xl'
                                       objectFit='contain'
                                       draggable={false}
                                       pointerEvents={"none"}
                                />
                            </ModalBody>
                        </ModalContent>
                    </Modal>
                </Box>
            )
        } else if (sourceUrl && ValidAudioFormats.includes(extension)) {
              let maxNumberOfReproductions = props.maxNumberOfReproductions ? props.maxNumberOfReproductions : 2;
              if (props.conditions){
                  const pattern = /AudioTimes(\d+)/g;
                  const patternExists = pattern.exec(props.conditions);
                  if (patternExists !== null && patternExists[1] !== null) {
                    const numberPattern = /(\d+)/g;
                    const numberExists = numberPattern.exec(patternExists[1]);
                    if (numberExists !== null && numberExists[1] !== null)
                        maxNumberOfReproductions = Number(numberExists[1]);
                  }
              }
            return (
                <AudioPlayer ref={audioPlayerRef}
                    sourceUrl={sourceUrl}
                    timesPlayed={props.timesPlayed}
                    onAudioPlayed={props.onAudioPlayed}
                    onAudioEnded={props.onAudioEnded}
                    maxNumberOfReproductions={maxNumberOfReproductions}
                    autoPlay={!!(props.autoPlay && sourceUrl)}
                    noOutline={props.noOutline}
                    hidePlayButton={props.hidePlayButton}
                    hidden={props.hidden}
                />
            )
        } else {
            return (
                <Markdown
                    content={props.text + " " + props.resource}
                    compact={props.compact}
                />
            )
        }
    }
}
);


export default RenderResource;
